import { create } from 'zustand';
import type { RiskEvent, RiskEventType } from '@/realtime/types';

export type AlertSeverity = 'INFO' | 'WARN' | 'CRIT';

export interface Alert {
  id: string;
  type: RiskEventType | 'EMERGENCY_STOP_SET' | 'EMERGENCY_STOP_CLEARED' | 'FORCE_LIQUIDATION';
  severity: AlertSeverity;
  title: string;
  detail?: string;
  occurredAt: string;
  ack: boolean;
  ackAt?: string;
  payload?: Record<string, unknown>;
}

interface State {
  alerts: Alert[];
  push: (a: Omit<Alert, 'ack'>) => void;
  pushFromRiskEvent: (e: RiskEvent) => void;
  ack: (id: string) => void;
  /** 归档(置已确认)所有 id 以 prefix 起头的告警 — 用于停机解除后清掉活跃的「紧急停机已激活」。 */
  ackByPrefix: (prefix: string) => void;
  ackAll: () => void;
  clear: () => void;
}

/** 触发方 → 中文(覆盖停机 AUTO_RISK/MANUAL_ADMIN 与强平 SCHEDULED/EMERGENCY/MANUAL 两套取值)。 */
export function triggeredByCn(by?: string): string {
  switch (by) {
    case 'MANUAL_ADMIN':
      return '管理员手动';
    case 'AUTO_RISK':
      return '风控自动';
    case 'SCHEDULED':
      return '定时';
    case 'EMERGENCY':
      return '紧急风控';
    case 'MANUAL':
      return '手动';
    default:
      return by ?? '未知';
  }
}

/** 已知 reason 码 → 中文(payload.reason 多为机器码,直接展示不可读)。 */
const REASON_CN: Record<string, string> = {
  EQUITY_LOW: '净值跌破水位线',
  DAILY_LOSS: '当日累计亏损越上限',
  API_ERROR_SPIKE: 'cTrader API 错误率越线',
  MARGIN_OVER_LIMIT: '保证金占用达/超上限',
  MARGIN_APPROACHING_LIMIT: '保证金占用接近上限',
  MAIN_LIQUIDATION_FAILED: '强平后真实账户仍有未平持仓,保持停机待人工处置',
  SNAPSHOT_EXPIRED: '风控快照过期(cTrader 可能断线)',
  NO_SNAPSHOT: '无风控快照',
  INITIAL_BALANCE_INVALID: '初始余额未正确初始化',
  PARSE_FAILED: '净值/余额解析失败',
  DB_ERROR: '读取风控快照失败',
};

function pct(v: unknown): string | null {
  return typeof v === 'number' ? `${v.toFixed(1)}%` : null;
}

/**
 * risk_events payload → 人类可读中文 detail(替代裸 JSON.stringify,便于定位)。
 * 已知类型走专用模板;未知类型回退「reason 码翻译 + 紧凑 key=value」,绝不再吐整段 JSON。
 */
export function formatRiskDetail(type: string, payload?: Record<string, unknown>): string | undefined {
  if (!payload || Object.keys(payload).length === 0) return undefined;
  const p = payload;
  const reasonCode = typeof p.reason === 'string' ? p.reason : undefined;
  switch (type) {
    case 'EQUITY_LOW': {
      const a = pct(p.equityPct);
      const b = pct(p.floorPct);
      if (a && b) return `净值水位 ${a} < 下限 ${b}`;
      break;
    }
    case 'DAILY_LOSS': {
      const a = pct(p.dailyLossPct);
      const b = pct(p.maxPct);
      if (a && b) return `当日亏损 ${a} > 上限 ${b}`;
      break;
    }
    case 'MARGIN_HIGH': {
      const a = pct(p.marginUsagePct);
      const b = pct(p.limitPct);
      if (a && b) {
        const tag = reasonCode === 'MARGIN_OVER_LIMIT' ? '达/超上限' : '接近上限';
        return `保证金占用 ${a}(${tag} ${b})`;
      }
      break;
    }
    case 'API_ERROR_SPIKE': {
      const a = pct(p.apiErrorRatePct);
      const b = pct(p.thresholdPct);
      if (a && b) {
        const n = typeof p.sampleCount === 'number' ? `,近2分钟 ${p.sampleCount} 次调用` : '';
        return `API 错误率 ${a} > 阈值 ${b}${n}`;
      }
      break;
    }
    case 'EMERGENCY_STOP': {
      const head = reasonCode ? (REASON_CN[reasonCode] ?? reasonCode) : '风控自动停机';
      const failures = Array.isArray(p.failures) ? p.failures : [];
      if (failures.length > 0) {
        const f = failures
          .slice(0, 3)
          .map((x) => {
            const o = (x ?? {}) as Record<string, unknown>;
            return `仓#${o.position_id ?? '?'}(${o.error ?? '?'})`;
          })
          .join('、');
        return `${head} · 失败 ${failures.length} 笔:${f}`;
      }
      return head;
    }
    case 'CAPACITY_WATCH':
    case 'CAPACITY_WARNING':
    case 'CAPACITY_DANGER': {
      const a = pct(p.marginUsagePct);
      if (a) return `真实账户容量占用 ${a}(分级=${p.level ?? '?'})`;
      break;
    }
    case 'VIRTUAL_STOPOUT':
      return `用户 ${p.userId ?? '?'} 虚拟爆仓,平掉 ${p.closedCount ?? '?'} 笔虚拟持仓`;
    case 'RISK_DATA_STALE':
      return `风控数据失效:${reasonCode ? (REASON_CN[reasonCode] ?? reasonCode) : '未知'}`;
  }
  // 兜底:reason 码翻译 + 紧凑 key=value(不再裸 JSON)。
  const parts: string[] = [];
  if (reasonCode) parts.push(REASON_CN[reasonCode] ?? reasonCode);
  for (const [k, v] of Object.entries(p)) {
    if (k === 'reason' || v == null || typeof v === 'object') continue;
    parts.push(`${k}=${v}`);
  }
  return parts.length > 0 ? parts.join(' · ') : JSON.stringify(p);
}

/** 后端 severity('INFO'/'WARN'/'CRITICAL')→ UI 内部展示('INFO'/'WARN'/'CRIT')。 */
function toUiSeverity(s: 'INFO' | 'WARN' | 'CRITICAL' | undefined): AlertSeverity | undefined {
  if (s === undefined) return undefined;
  return s === 'CRITICAL' ? 'CRIT' : s;
}

const SEVERITY_BY_TYPE: Record<string, AlertSeverity> = {
  ORDER_BLOCKED: 'WARN',
  EMERGENCY_STOP: 'CRIT',
  EMERGENCY_STOP_SET: 'CRIT',
  EMERGENCY_STOP_CLEARED: 'INFO',
  FORCE_LIQUIDATION: 'CRIT',
  ABNORMAL_VOLUME: 'WARN',
  MARGIN_HIGH: 'WARN',
  EQUITY_LOW: 'CRIT',
  API_ERROR_SPIKE: 'WARN',
};

const TITLE_BY_TYPE: Record<string, string> = {
  ORDER_BLOCKED: '跟单引擎拦截下单',
  EMERGENCY_STOP: '风控自动触发紧急停机',
  EMERGENCY_STOP_SET: '紧急停机已激活',
  EMERGENCY_STOP_CLEARED: '紧急停机已解除',
  FORCE_LIQUIDATION: '一键强制平仓已触发',
  ABNORMAL_VOLUME: '异常单量',
  MARGIN_HIGH: '保证金占用接近上限',
  EQUITY_LOW: '净值跌破水位线',
  API_ERROR_SPIKE: 'cTrader API 错误率异常',
};

export const useAlertsStore = create<State>()((set) => ({
  alerts: [],
  push: (a) =>
    set((s) => {
      // 同 id 去重:紧急停机快照帧在每次订阅/重连都会补发(id 按 triggeredAt 派生),
      // 不去重则重连一次多一条「紧急停机已激活」,活跃数虚高、难定位。
      if (s.alerts.some((x) => x.id === a.id)) return s;
      return { alerts: [{ ack: false, ...a } as Alert, ...s.alerts].slice(0, 200) };
    }),
  pushFromRiskEvent: (e) =>
    set((s) => {
      // 服务端若带 id 则复用(WS 与 REST /risk-events 同源,便于去重),否则本地生成。
      const id = e.id ?? `${e.type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      // 已存在(同 id)则忽略,避免实时推送与历史查询重复入栈。
      if (e.id && s.alerts.some((a) => a.id === e.id)) return s;
      const a: Alert = {
        id,
        type: e.type,
        // events.md §6:severity 为后端全大写取值(INFO/WARN/CRITICAL),
        // 这里归一到 UI 内部展示用的 'CRIT'。
        severity: toUiSeverity(e.severity) ?? SEVERITY_BY_TYPE[e.type] ?? 'WARN',
        title: TITLE_BY_TYPE[e.type] ?? e.type,
        detail: formatRiskDetail(e.type, e.payload),
        // events.md §6:时间字段为 createdAt(ISO8601)。
        occurredAt: e.createdAt ?? new Date().toISOString(),
        ack: false,
        payload: e.payload,
      };
      return { alerts: [a, ...s.alerts].slice(0, 200) };
    }),
  ack: (id) =>
    set((s) => ({
      alerts: s.alerts.map((a) => (a.id === id ? { ...a, ack: true, ackAt: new Date().toISOString() } : a)),
    })),
  ackByPrefix: (prefix) =>
    set((s) => {
      const now = new Date().toISOString();
      return {
        alerts: s.alerts.map((a) =>
          a.id.startsWith(prefix) && !a.ack ? { ...a, ack: true, ackAt: now } : a,
        ),
      };
    }),
  ackAll: () =>
    set((s) => {
      const now = new Date().toISOString();
      return { alerts: s.alerts.map((a) => (a.ack ? a : { ...a, ack: true, ackAt: now })) };
    }),
  clear: () => set({ alerts: [] }),
}));

/** 启动期注入若干历史告警 fixture(dev 期可见) */
export function seedHistoricalAlerts(): void {
  const seeds: Omit<Alert, 'ack'>[] = [
    {
      id: 'seed-1',
      type: 'API_ERROR_SPIKE',
      severity: 'WARN',
      title: TITLE_BY_TYPE.API_ERROR_SPIKE!,
      detail: 'rate=12.4% threshold=10% window=60s',
      occurredAt: new Date(Date.now() - 4 * 3600_000).toISOString(),
    },
    {
      id: 'seed-2',
      type: 'ORDER_BLOCKED',
      severity: 'WARN',
      title: TITLE_BY_TYPE.ORDER_BLOCKED!,
      detail: '主账户 XAUUSD 总持仓 1.85 + 0.20 > 2.00 lot 上限',
      occurredAt: new Date(Date.now() - 2.6 * 3600_000).toISOString(),
    },
    {
      id: 'seed-3',
      type: 'MARGIN_HIGH',
      severity: 'WARN',
      title: TITLE_BY_TYPE.MARGIN_HIGH!,
      detail: '保证金占用 47.3% 接近 50% 阈值',
      occurredAt: new Date(Date.now() - 1.2 * 3600_000).toISOString(),
    },
  ];
  const acked = seeds.map((s, i) => ({
    ...s,
    ack: i < 2,
    ackAt: i < 2 ? new Date(Date.now() - 1 * 3600_000).toISOString() : undefined,
  })) as Alert[];
  useAlertsStore.setState({ alerts: acked });
}
